import React, { Component } from 'react';
import { docsSoap } from 'docs-soap';
import PropTypes from 'prop-types';
import Immutable from 'immutable';
import { FormGroup, ControlLabel, FormControl } from 'react-bootstrap';
import Draft, { RichUtils, convertToRaw, EditorState, convertFromRaw, ContentState,
  Entity, CompositeDecorator, Editor, AtomicBlockUtils, DefaultDraftBlockRenderMap, Modifier, convertFromHTML } from 'draft-js';
import browserImageSize from 'browser-image-size';
import CodeUtils from 'draft-js-code';
import BlockStyleControls from './BlockStyleControls';
import HeadingStyleControls from './HeadingStyleControls';
import InlineStyleControls from './InlineStyleControls';
import EditorLink from './EditorLink';
import EditorImageControls from './EditorImageControls';
import EditorYoutubeEmbedControls from './EditorYoutubeEmbedControls';
import EditorTweetEmbedControls from './EditorTweetEmbedControls';
import EditorImage from './EditorImage';
import EditorHTML from './EditorHTML';
import EditorLinkDisplay from './EditorLinkDisplay';
import BlitzAPI from '../../../api/BlitzAPI';
import './EntryEditor.css';

function getBlockStyle(block) {
  switch (block.getType()) {
    case 'blockquote': return 'entry-editor-blockquote';
    default: return null;
  }
}

const blockRenderMap = DefaultDraftBlockRenderMap.merge(Immutable.Map({
  atomic: {
    element: 'div',
  },
  'code-block': {
    element: 'div',
  },
}));


const styleMap = {
  CODE: {
    backgroundColor: 'rgba(0, 0, 0, 0.05)',
    fontFamily: '"Inconsolata", "Menlo", "Consolas", monospace',
    fontSize: 16,
    padding: 2,
  },
};

function findLinkEntities(contentBlock, callback, contentState) {
  contentBlock.findEntityRanges(
    (character) => {
      const entityKey = character.getEntity();
      return (
        entityKey !== null &&
        contentState.getEntity(entityKey).getType() === 'LINK'
      );
    },
    callback,
  );
}


export default class FormEditor extends Component {
  constructor(props) {
    super(props);
    this.onTab = this.onTab.bind(this);
    this.handleKeyCommand = this.handleKeyCommand.bind(this);
    this.toggleBlockType = this.toggleBlockType.bind(this);
    this.toggleInlineStyle = this.toggleInlineStyle.bind(this);
    this.onLinkCreate = this.onLinkCreate.bind(this);
    this.onLinkRemove = this.onLinkRemove.bind(this);
    this.onImageAdd = this.onImageAdd.bind(this);
    this.onYoutubeAdd = this.onYoutubeAdd.bind(this);
    this.onTweetAdd = this.onTweetAdd.bind(this);
    this.onBlur = this.onBlur.bind(this);
    this.onChange = this.onChange.bind(this);
    this.handleDrop = this.handleDrop.bind(this);
    this.handleReturn = this.handleReturn.bind(this);
    this.keyBindingFn = this.keyBindingFn.bind(this);
    this.handleBeforeInput = this.handleBeforeInput.bind(this);
    this.handlePastedText = this.handlePastedText.bind(this);
    this.blockRenderer = this.blockRenderer.bind(this);
    this.setReadOnly = this.setReadOnly.bind(this);
    this.resetSelection = this.resetSelection.bind(this);
    this.setSelection = this.setSelection.bind(this);
    this.handleDroppedFiles = this.handleDroppedFiles.bind(this);
    this.decorator = new CompositeDecorator([
      {
        strategy: findLinkEntities,
        component: EditorLinkDisplay,
      },
    ]);

    let editorState;

    if (this.props.input.value) {
      editorState = EditorState.createWithContent(
        convertFromRaw(this.props.input.value), this.decorator,
      );
    } else {
      editorState = EditorState.createEmpty(this.decorator);
    }

    this.state = {
      editorState,
      readOnly: false,
    };

    // Take the input value and convert into the component state. Then onComponent Blur,
    // update the original value.
  }

  onBlur() {
    const contentState = this.state.editorState.getCurrentContent();
    this.props.input.onChange(convertToRaw(contentState));
  }

  onChange(editorState, cb) {
    this.setState({ editorState }, cb);
  }

  onTab(e) {
    const maxDepth = 4;
    this.props.input.onChange(RichUtils.onTab(e, this.props.input.value, maxDepth));
  }

  onLinkCreate(urlValue) {
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    const entityKey = Entity.create('LINK', 'MUTABLE', { url: urlValue });
    const newState = RichUtils.toggleLink(
      editorState,
      selection,
      entityKey);
    this.setState(
      { editorState: newState },
      () => setTimeout(() => this.editorRef.focus(), 0),
    );
  }

  onLinkRemove() {
    const { editorState } = this.state;
    const selection = editorState.getSelection();
    const newState = RichUtils.toggleLink(
      editorState,
      selection,
      null);
    this.onChange(newState);
  }

  onImageAdd(url, caption, style, alt, size) {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'image',
      'IMMUTABLE',
      { src: url, caption, style, alt, size },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(
      editorState,
      { currentContent: contentStateWithEntity },
    );

    this.setState({
      editorState: AtomicBlockUtils.insertAtomicBlock(
        newEditorState,
        entityKey,
        ' ',
      ),
      showURLInput: false,
      urlValue: '',
    });
  }

  onYoutubeAdd(url) {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    const contentStateWithEntity = contentState.createEntity(
      'youtube',
      'IMMUTABLE',
      { src: url },
    );
    const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
    const newEditorState = EditorState.set(
      editorState,
      { currentContent: contentStateWithEntity },
    );

    this.setState({
      editorState: AtomicBlockUtils.insertAtomicBlock(
        newEditorState,
        entityKey,
        ' ',
      ),
      showURLInput: false,
      urlValue: '',
    });
  }

  onTweetAdd(url) {
    const { editorState } = this.state;
    const contentState = editorState.getCurrentContent();
    BlitzAPI.getTwitterEmbed({ url }).then((resp) => {
      const contentStateWithEntity = contentState.createEntity(
        'tweet',
        'IMMUTABLE',
        { src: url, tweet: resp.data },
      );
      const entityKey = contentStateWithEntity.getLastCreatedEntityKey();
      const newEditorState = EditorState.set(
        editorState,
        { currentContent: contentStateWithEntity },
      );

      this.setState({
        editorState: AtomicBlockUtils.insertAtomicBlock(
          newEditorState,
          entityKey,
          ' ',
        ),
        showURLInput: false,
        urlValue: '',
      });
    });
  }

  resetSelection() {
    const { editorState } = this.state;

    this.onChange(EditorState.moveFocusToEnd(editorState));
  }

  setSelection(selection) {
    const { editorState } = this.state;
    this.onChange(EditorState.forceSelection(editorState, selection));
  }

  blockRenderer(block) {
    const { editorState } = this.state;
    if (block.getType() === 'atomic') {
      return {
        component: EditorImage,
        props: {
          editorState,
          onEditorChange: this.onChange,
          selection: editorState.getSelection(),
          setReadOnly: this.setReadOnly,
          resetSelection: this.resetSelection,
          setSelection: this.setSelection,
        },
      };
    }

    if (block.getType() === 'code-block') {
      return {
        component: EditorHTML,
        editable: true,
      };
    }

    return null;
  }

  setReadOnly(bool) {
    this.setState({ readOnly: bool });
  }

  handleReturn(e) {
    const { editorState } = this.state;
    if (e.shiftKey) {
      console.log('Here in shift key');
      // Look at the jumping here.
      this.setState({ editorState: RichUtils.insertSoftNewline(editorState) });
      return 'handled';
    }
    return 'not-handled';
  }

  handleDrop(selection, dataTransfer, isInternal) {
    const { editorState } = this.state;
    const currentSelection = editorState.getSelection();
    const contentState = editorState.getCurrentContent();
    const contentBlock = contentState.getBlockForKey(currentSelection.getFocusKey());

    if (contentBlock) {
      const blockEntityType = contentBlock.getEntityAt(0);
      if (!blockEntityType) {
        return 'handled';
      }
      const entity = contentState.getEntity(blockEntityType);
      if (entity.type === 'image') {
        this.setState({
          editorState: AtomicBlockUtils.moveAtomicBlock(editorState, contentBlock, selection),
        });
        return 'handled';
      }
      return 'not-handled';
    }
    return 'not-handled';
  }

  handleKeyCommand(command) {
    const { editorState } = this.state;
    let newState;

    if (CodeUtils.hasSelectionInBlock(editorState)) {
      newState = CodeUtils.handleKeyCommand(editorState, command);
    }


    if (!newState) {
      newState = RichUtils.handleKeyCommand(editorState, command);
    }

    if (newState) {
      this.onChange(newState);
      return true;
    }
    return false;
  }

  handleBeforeInput(chars) {
    switch (chars) {
      case '\u201d':
      case '\u201c':
        const { editorState } = this.state;
        const selection = editorState.getSelection();
        const contentState = editorState.getCurrentContent();
        let newContent;
        if (selection.size === 0) {
          newContent = Modifier.insertText(contentState, selection, '"');
        } else {
          newContent = Modifier.replaceText(contentState, selection, '"');
        }
        this.onChange(EditorState.push(editorState, newContent, 'insert-characters'));
        return 'handled';
      default:
        return 'not-handled';
    }
  }

  handlePastedText(text, html, edState) {
    if (html && (html.includes('\u201d') || html.includes('\u201c'))) {
      const newHtml = (html.replace('\u201d', '"').replace('\u201c', '"'));
      // Clean the HTML

      const clean = docsSoap(newHtml);
      const { editorState } = this.state;
      const selection = editorState.getSelection();
      const contentState = editorState.getCurrentContent();

      // Convert HTML to ContentState
      const blocksFromHTML = convertFromHTML(clean);
      const tempContentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );

      // create new content
      const newContent = Modifier.replaceWithFragment(
        contentState,
        selection,
        tempContentState.getBlockMap(),
      );

      this.onChange(EditorState.push(editorState, newContent, 'insert-fragment'));
    } else if (html) {
      // Clean the HTML

      const clean = docsSoap(html);
      const { editorState } = this.state;
      const selection = editorState.getSelection();
      const contentState = editorState.getCurrentContent();

      // Convert HTML to ContentState
      const blocksFromHTML = convertFromHTML(clean);
      const tempContentState = ContentState.createFromBlockArray(
        blocksFromHTML.contentBlocks,
        blocksFromHTML.entityMap,
      );

      // create new content
      const newContent = Modifier.replaceWithFragment(
        contentState,
        selection,
        tempContentState.getBlockMap(),
      );

      this.onChange(EditorState.push(editorState, newContent, 'insert-fragment'));
    } else if (text.includes('\u201d') || text.includes('\u201c')) {
      const newText = (text.replace('\u201d', '"').replace('\u201c', '"'));
      const { editorState } = this.state;
      const selection = editorState.getSelection();
      const contentState = editorState.getCurrentContent();
      let newContent;
      if (selection.size === 0) {
        newContent = Modifier.insertText(contentState, selection, newText);
      } else {
        newContent = Modifier.replaceText(contentState, selection, newText);
      }
      this.onChange(EditorState.push(editorState, newContent, 'insert-characters'));

      return true;
    }
    return false;
  }

  handleDroppedFiles(selection, files) {
    const { editorState } = this.state;
    this.onChange(
      EditorState.forceSelection(editorState, selection),
      () => {
        files.map(
          (f) => {
            const formData = new FormData();
            formData.append('file', f);
            return BlitzAPI.postEntryImage(formData).then(
              (response) => {
                const url = response.data.image_url;
                browserImageSize(url).then(
                  size => this.onImageAdd(url, '', 'regular', '', size),
                );
              },
            );
          },
        );
      },
    );
  }

  toggleBlockType(blockType) {
    this.onChange(
      RichUtils.toggleBlockType(
        this.state.editorState,
        blockType,
      ),
    );
  }

  keyBindingFn(e) {
    const { editorState } = this.state;
    let command;

    if (CodeUtils.hasSelectionInBlock(editorState)) {
      command = CodeUtils.getKeyBinding(e);
    }
    if (command) {
      return command;
    }

    return Draft.getDefaultKeyBinding(e);
  }

  toggleInlineStyle(inlineStyle) {
    this.onChange(
      RichUtils.toggleInlineStyle(
        this.state.editorState,
        inlineStyle,
      ),
    );
  }

  render() {
    const { label, controlId } = this.props;

    const { editorState, readOnly } = this.state;

    let className = 'entry-editor-editor';
    const contentState = editorState.getCurrentContent();
    if (!contentState.hasText()) {
      if (contentState.getBlockMap().first().getType() !== 'unstyled') {
        className += ' entry-editor-hidePlaceholder';
      }
    }

    return (
      <FormGroup controlId={controlId || ''} onBlur={this.onBlur}>
        <ControlLabel>{label}</ControlLabel>
        <div className="entry-editor-root">
          <div className="entry-editor-controls-container">
            <InlineStyleControls
              editorState={editorState}
              onToggle={this.toggleInlineStyle}
            />
            <HeadingStyleControls
              editorState={editorState}
              onToggle={this.toggleBlockType}
            />
            <BlockStyleControls
              editorState={editorState}
              onToggle={this.toggleBlockType}
            />
            <EditorLink
              editorState={editorState}
              container={this}
              onLinkCreate={this.onLinkCreate}
              onLinkRemove={this.onLinkRemove}
            />
            <EditorImageControls
              editorState={editorState}
              container={this}
              onImageAdd={this.onImageAdd}
            />
            <EditorYoutubeEmbedControls
              container={this}
              onYoutubeAdd={this.onYoutubeAdd}
            />
            <EditorTweetEmbedControls
              container={this}
              onTweetAdd={this.onTweetAdd}
            />
          </div>
          <div className={className}>
            <Editor
              blockStyleFn={getBlockStyle}
              blockRenderMap={blockRenderMap}
              blockRendererFn={this.blockRenderer}
              customStyleMap={styleMap}
              editorState={editorState}
              handleKeyCommand={this.handleKeyCommand}
              keyBindingFn={this.keyBindingFn}
              onTab={this.onTab}
              ref={(editor) => { this.editorRef = editor; }}
              spellCheck
              handleDrop={this.handleDrop}
              onChange={this.onChange}
              handleReturn={this.handleReturn}
              handleDroppedFiles={this.handleDroppedFiles}
              placeholder="Start Writing..."
              handleBeforeInput={this.handleBeforeInput}
              handlePastedText={this.handlePastedText}
              readOnly={readOnly}
            />
          </div>
        </div>
        <FormControl.Feedback />
      </FormGroup>
    );
  }
}

FormEditor.propTypes = {
  label: PropTypes.string,
  controlId: PropTypes.string,
  input: PropTypes.object,
  meta: PropTypes.object,
  options: PropTypes.array,
};



// WEBPACK FOOTER //
// ./src/cms/form/editor/FormEditor.js